import React, { useState, useCallback } from 'react'
import AdminAPI from 'api/AdminAPI'
import debounce from 'lodash.debounce'
import AdminEditFeaturedSchoolView from './AdminEditFeaturedSchoolView'
import PageWrapper from 'wrappers/PageWrapper/PageWrapper'

const AdminEditFeaturedSchool = (props) => {
    const { featuredSchool = {} } = props
    const [state, setState] = useState({
        ...featuredSchool,
        email: featuredSchool.email || '',
        name: featuredSchool.name || '',
        description: featuredSchool.description || '',
        card_description: featuredSchool.card_description || '',
        blurb_title: featuredSchool.blurb_title || '',
        blurb_description: featuredSchool.blurb_description || '',
        cta_faculties_title: featuredSchool.cta_faculties_title || '',
        cta_faculties_description: featuredSchool.cta_faculties_description || '',
        cta_faculties_url_title: featuredSchool.cta_faculties_url_title || '',
        cta_faculties_url: featuredSchool.cta_faculties_url || '',
        url: featuredSchool.url || '',
        url_tiktok: featuredSchool.url_tiktok || '',
        url_linkedin: featuredSchool.url_linkedin || '',
        url_instagram: featuredSchool.url_instagram || '',
        url_facebook: featuredSchool.url_facebook || '',
        program_types: featuredSchool.program_types || [],
        en: featuredSchool.en,
        fr: featuredSchool.fr,
        nl: featuredSchool.nl,
        language: featuredSchool.language || '',
        active: featuredSchool.active || false,
        paid: featuredSchool.paid || false
    })
    const [logo, setLogo] = useState(featuredSchool.logo || null)
    const [bannerImage, setBannerImage] = useState(featuredSchool.banner_image || null)
    const [cardImage, setCardImage] = useState(featuredSchool.card_image || null)
    const [campusMapImage, setCampusMapImage] = useState(featuredSchool.campus_map_image || null)
    const [imageArray, setImageArray] = useState(featuredSchool.images || [])
    const [schoolFacts, setSchoolFacts] = useState(featuredSchool.school_facts || [])
    const [faculties, setFaculties] = useState(featuredSchool.faculties || [])
    const [featuredCampuses, setFeaturedCampuses] = useState(featuredSchool.featured_campuses || [])
    const [faqs, setFaqs] = useState(featuredSchool.faqs || [])
    const [feedback, setFeedback] = useState({
        open: false,
        message: null,
        severity: null
    })
    const [validationErrors, setValidationErrors] = useState({})

    const validateForm = () => {
        const errors = {}
        const validations = [
            { field: 'name', value: state.name, message: 'Name is required' },
            { field: 'description', value: state.description?.replace(/<[^>]+>/g, ''), message: 'Description is required' },
            { field: 'card_description', value: state.card_description?.replace(/<[^>]+>/g, ''), message: 'Card description is required' },
            { field: 'program_types', value: state.program_types?.length, message: 'At least one program type is required' },
            { field: 'language', value: state.language, message: 'Language is required' }
        ]
        
        validations.forEach(({ field, value, message }) => {
            const isEmptyString = typeof value === 'string' && value.trim() === ''
            const isEmptyProgramTypes = field === 'program_types' && value === 0
            const isEmptyValue = !value || isEmptyString || isEmptyProgramTypes
            
            if (isEmptyValue) {
                errors[field] = message
            }
        })
        const validCampuses = featuredCampuses.filter(campus => campus.city?.name?.trim())
        if (validCampuses.length === 0) {
            errors.campuses = 'At least one campus with a valid city is required'
        }
        
        return errors
    }

    const closeFeedback = () => { setFeedback({ ...feedback, open: false }) }
    const handleChange = (e) => { 
        setState({ ...state, [e.target.name]: e.target.value })
        if (validationErrors[e.target.name]) {
            setValidationErrors({ ...validationErrors, [e.target.name]: undefined })
        }
    }
    const handleBannerImageAltChange = (e) => { setBannerImage({ ...bannerImage, alt: e.target.value }) }
    const handleCardImageAltChange = (e) => { setCardImage({ ...cardImage, alt: e.target.value }) }
    const debouncedHandleQuillChange = useCallback(
        debounce((value, name) => {
            setState(prevState => ({ ...prevState, [name]: value }))
            if (validationErrors[name]) {
                setValidationErrors({ ...validationErrors, [name]: undefined })
            }
        }, 300), [validationErrors]
    )

    const handleSelectedItems = (selectedItems, attribute) => {
        const formattedItems = selectedItems.map(item => item.value)
        setState({ ...state, [attribute]: formattedItems })
        if (validationErrors[attribute]) {
            setValidationErrors({ ...validationErrors, [attribute]: undefined })
        }
    }

    const handleSubmit = (event) => {
        event.preventDefault()
        
        const errors = validateForm()
        if (Object.keys(errors).length > 0) {
            setValidationErrors(errors)
            setFeedback({
                open: true,
                message: 'Please fix the validation errors before submitting',
                severity: 'error'
            })
            return
        }
        
        setValidationErrors({})
        
        const data = {
            ...state,
            images: imageArray,
            logo: logo,
            banner_image: bannerImage,
            card_image: cardImage,
            campus_map_image: campusMapImage,
            school_facts: schoolFacts,
            faculties: faculties,
            featured_campuses: featuredCampuses,
            faqs: faqs
        }
        AdminAPI.UpdateFeaturedSchool(data).then((response) => {
            const { is_success, errors, redirection_url } = response.data
            if (is_success) {
                setFeedback({
                    open: true,
                    message: 'Successfully updated your featured school',
                    severity: 'success'
                })
                setTimeout(() => {
                    window.location.href = redirection_url
                }, 500)
            } else {
                setFeedback({
                    open: true,
                    message: `Error occurred: ${errors}`,
                    severity: 'error'
                })
            }
        })
    }

    return (
        <PageWrapper disableFooter disableFooterLists disableNavbar>
            <AdminEditFeaturedSchoolView
                state={state}
                setState={setState}
                feedback={feedback}
                closeFeedback={closeFeedback}
                handleChange={handleChange}
                handleBannerImageAltChange={handleBannerImageAltChange}
                handleSubmit={handleSubmit}
                debouncedHandleQuillChange={debouncedHandleQuillChange}
                handleSelectedItems={handleSelectedItems}
                imageArray={imageArray}
                setImageArray={setImageArray}
                logo={logo}
                setLogo={setLogo}
                bannerImage={bannerImage}
                setBannerImage={setBannerImage}
                cardImage={cardImage}
                setCardImage={setCardImage}
                handleCardImageAltChange={handleCardImageAltChange}
                campusMapImage={campusMapImage}
                setCampusMapImage={setCampusMapImage}
                schoolFacts={schoolFacts}
                setSchoolFacts={setSchoolFacts}
                faculties={faculties}
                setFaculties={setFaculties}
                featuredCampuses={featuredCampuses}
                setFeaturedCampuses={(newCampuses) => {
                    setFeaturedCampuses(newCampuses)
                    if (validationErrors.campuses) {
                        setValidationErrors({ ...validationErrors, campuses: undefined })
                    }
                }}
                faqs={faqs}
                setFaqs={setFaqs}
                validationErrors={validationErrors}
            />
        </PageWrapper>
    )
}

export default AdminEditFeaturedSchool
